/**
 * Game Constants & Configuration
 * Centralized location for all hard-coded game values
 * 
 * Phase 1 Refactoring - Extracted from useGameActions.js
 */

// ========== COUNTRY POSITIONS (Map coordinates in %) ==========
export const COUNTRY_POSITIONS = {
  'USA': { x: 20, y: 35 },
  'RUS': { x: 65, y: 25 },
  'CHN': { x: 75, y: 40 },
  'EU': { x: 48, y: 30 },
  'NK': { x: 82, y: 38 }
};

// ========== GEOGRAPHICAL NEIGHBORS (for nuclear tension effects) ==========
export const COUNTRY_NEIGHBORS = {
  'USA': ['EU'],
  'RUS': ['EU', 'CHN', 'NK'],
  'CHN': ['RUS', 'NK'],
  'EU': ['RUS', 'USA'],
  'NK': ['CHN', 'RUS']
};

// ========== ACTION NAMES (for display/logging) ==========
export const ACTION_NAMES = {
  'propaganda': 'PROPAGANDA',
  'blackmail': 'BLACKMAIL',
  'diplomacy': 'DIPLOMACY',
  'deploy_troops': 'DEPLOY TROOPS',
  'air_strike': 'AIR STRIKE',
  'nuclear_strike': 'NUCLEAR STRIKE',
  'radiation_damage': 'RADIATION DAMAGE'
};

// ========== ANIMATION DURATIONS (milliseconds) ==========
export const ANIMATION_DURATIONS = {
  // Player actions
  propaganda: 5000,
  propagandaCelebration: 5000,
  diplomacy: 5000,
  blackmail: 5000,
  deployTroops: 6000,
  airStrikeSuccess: 11500,  // Updated for slower cinematic bomber flight + full return journey
  airStrikeFailed: 15000,  // ✅ EXTENDED: 5s explosion + 10s ejection video = 15s total (extended to let video load/play)
  nuclearStrike: 21000,
  
  // AI actions (longer for better visibility)
  aiPropaganda: 8000,
  aiDiplomacy: 5000,
  aiBlackmail: 7000,
  aiDeployTroops: 8000,
  aiAirStrikeSuccess: 8500,
  aiAirStrikeFailed: 5000,
  aiNuclearStrike: 25000,
  aiRadiationDamage: 3000,
  
  // Dialogue timings
  propagandaDialogue: 6000,
  diplomacyDialogue: 4000,
  blackmailDialogue: 6000,
  deployTroopsDialogue: 7000,
  airStrikeDialogue: 7500,
  airStrikeFailedDialogue: 4000,
  
  // Delays
  gapBetweenAiActions: 2500,
  airStrikeDelayAfterAnimation: 2000,
  autoEndTurnDelay: 500,
  deployTroopsConfirmationDelay: 100,
  leaderAssassinationDelay: 1000,
  targetSpeaksAfterAttack: 2000,
  bomberFlashDuration: 3000
};

// ========== AUDIO FILE PATHS ==========
export const AUDIO_PATHS = {
  // Success/Failure Sounds
  successCheers: [
    '/assets/audio/success-cheer-1.mp3',
    '/assets/audio/success-cheer-2.mp3',
    '/assets/audio/success-cheer-3.mp3'
  ],
  errorBeep: '/assets/audio/error-beep.mp3',
  cheer: '/assets/audio/success-cheer-1.mp3',  // ✅ FIXED: Use existing success cheer file
  boo: '/assets/audio/nuke-blast.mp3',          // ✅ FIXED: Use explosion sound for failure (no dedicated boo file exists)
  
  // Air Strike
  airStrikeAvailable: '/assets/audio/airstrike-available.mp3',
  aiAirStrikeSiren: '/assets/audio/ai-airstrike-siren.mp3',
  
  // Train Military
  trainMilitary: '/assets/audio/train-military.mp3',
  cashRegister: 'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/0t2f8105_cash-register.mp3',  // Also plays for train military
  
  // Deploy Troops
  deployTroopsAvailable: 'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/beq7vat4_Troop%20Deployment%20Available%20Sir.mp3',
  
  // Enrichment Purchase
  cashRegister: 'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/0t2f8105_cash-register.mp3',
  
  // Nuclear
  nuclearLaunchAvailable: 'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/ke8vh05n_Nuclear%20Launch%20Available.mp3',
  nuclearLaunchDetected: '/assets/audio/nuclear-launch-detected.mp3',
  defeat: '/assets/audio/defeat-1.mp3',
  
  // Country-Specific Propaganda Sounds
  propaganda: {
    USA: [
      '/assets/audio/usa-propaganda-1.mp3',
      '/assets/audio/usa-propaganda-2-new.mp3',
      '/assets/audio/usa-propaganda-3.mp3',
      '/assets/audio/usa-propaganda-4.mp3',
      '/assets/audio/usa-propaganda-6-wars.mp3'
    ],
    EU: [
      '/assets/audio/propaganda-eu-1.mp3',
      '/assets/audio/eu-propaganda-2.mp3',
      '/assets/audio/eu-propaganda-3-russell.mp3',
      '/assets/audio/eu-propaganda-4-lucy.mp3',
      '/assets/audio/eu-propaganda-5-helmut1.mp3',
      '/assets/audio/eu-propaganda-6-genevieve.mp3',
      '/assets/audio/eu-propaganda-7-helmut2.mp3'
    ],
    RUS: [
      '/assets/audio/russia-propaganda-1-europe-weapons.mp3',
      '/assets/audio/russia-propaganda-2-english.mp3',
      '/assets/audio/russia-propaganda-3-weapon.mp3',
      '/assets/audio/russia-propaganda-4-alex1.mp3',
      '/assets/audio/russia-propaganda-5-alex2.mp3',
      '/assets/audio/russia-propaganda-6-arcades.mp3',
      '/assets/audio/russia-propaganda-7-markos.mp3'
    ],
    CHN: [
      '/assets/audio/china-propaganda-1.mp3',
      '/assets/audio/china-propaganda-2.mp3',
      '/assets/audio/china-propaganda-3.mp3',
      '/assets/audio/china-propaganda-1-anchor.mp3',
      '/assets/audio/china-propaganda-2-calm.mp3'
    ],
    NK: [
      '/assets/audio/nkorea-propaganda-1.mp3',
      '/assets/audio/nkorea-propaganda-2.mp3',
      '/assets/audio/nkorea-propaganda-3.mp3',
      '/assets/audio/nkorea-propaganda-4-kim1.mp3',
      '/assets/audio/nkorea-propaganda-5-us-aggression.mp3',
      '/assets/audio/nkorea-propaganda-6-kim4.mp3',
      '/assets/audio/nkorea-propaganda-7-kim5.mp3',
      '/assets/audio/nkorea-propaganda-8-kim-jong-il.mp3',
      '/assets/audio/nkorea-propaganda-9-nkprop2.mp3',
      '/assets/audio/nkorea-propaganda-10-nkprop.mp3'
    ]
  },
  
  // Country-Specific Blackmail Reaction Sounds
  blackmailReactions: {
    USA: [
      '/assets/audio/usa-blackmail-1-ep-files.mp3',
      '/assets/audio/usa-blackmail-2-moved.mp3',
      '/assets/audio/usa-blackmail-3-psy.mp3'
    ]
  }
};

// ========== AUDIO SETTINGS ==========
export const AUDIO_SETTINGS = {
  cheerVolume: 0.6,
  propagandaVolume: 1.0,
  propagandaStartTime: 1.0,
  blackmailReactionVolume: 0.7,
  blackmailReactionStartTime: 1.0,
  errorVolume: 0.7,
  feedbackVolume: 0.3,
  feedbackStartTime: 1.0,
  nuclearLaunchVolume: 1.0,
  defeatVolume: 0.5,
  // Availability Notifications
  airStrikeAvailableGain: 2.0, // 200% volume boost
  deployTroopsAvailableGain: 2.0, // 200% volume boost
  nuclearLaunchAvailableGain: 2.5, // 250% volume boost (important!)
  aiAirStrikeSirenVolume: 0.5
};

// ========== RESOURCE REQUIREMENTS ==========
export const RESOURCE_REQUIREMENTS = {
  airStrike: {
    money: 10,
    military: 5,
    influence: 5
  }
};

// ========== HELPER FUNCTIONS ==========

/**
 * Get random item from array
 */
export const getRandomItem = (array) => {
  return array[Math.floor(Math.random() * array.length)];
};

/**
 * Get country position (with fallback)
 */
export const getCountryPosition = (countryCode) => {
  return COUNTRY_POSITIONS[countryCode] || { x: 50, y: 50 };
};

/**
 * Get random propaganda sound for a country
 */
export const getRandomPropagandaSound = (countryCode) => {
  const sounds = AUDIO_PATHS.propaganda[countryCode];
  return sounds ? getRandomItem(sounds) : null;
};

/**
 * Get random blackmail reaction sound for a country
 */
export const getRandomBlackmailReactionSound = (countryCode) => {
  const sounds = AUDIO_PATHS.blackmailReactions[countryCode];
  return sounds ? getRandomItem(sounds) : null;
};

/**
 * Get random success cheer sound
 */
export const getRandomSuccessCheer = () => {
  return getRandomItem(AUDIO_PATHS.successCheers);
};

/**
 * Get action display name
 */
export const getActionName = (actionType) => {
  return ACTION_NAMES[actionType] || actionType.toUpperCase();
};
